<template lang="pug" src="./client-editor.pug"></template>

<script>
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';

import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        user: {
            firstName: null,
            lastName: null,
            middleName: null,
            phoneNumber: null,
            email: null,
            comment: null,
            brandId: null
        },
        clientDetails: {
            birthday: null
        },
        loaded: false,
        canSaveClient: false,
        birthdayModal: false,
        requestProcessing: false
    }),
    validations: {
        user: {
            firstName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            lastName: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            middleName: {
                minLength: minLength(2),
                maxLength: maxLength(30)
            },
            phoneNumber: {
                required
            },
            email: {
                email
            },
            comment: {
                maxLength: maxLength(255)
            }
        }
    },
    computed: {
        ...mapState({
            brands: state => state.data.brands
        }),
        ...mapGetters(['roleName', 'userBrandId', 'operator']),
        canEditClients() {
            return ['admin', 'boss', 'manager'].includes(this.roleName) || this.operator && this.operator.canEditClients;
        },
        saveMethod() {
            return this.user.id ? 'put' : 'post';
        },
        saveUrl() {
            const userId = this.user.id;

            return userId ? `/clients/${userId}` : '/clients';
        },
    },
    watch: {
        clientDetails: {
            handler() {
                this.canSaveClient = true;
            },
            deep: true
        },
        user: {
            handler() {
                this.canSaveClient = true;
            },
            deep: true
        }
    },
    created() {
        this.$store.dispatch('data/getBrands');

        if (this.userBrandId) {
            this.user.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;
            this.loadData();
        }
    },
    methods: {
        async loadData() {
            await request({ method: 'get', url: `/clients/${this.$route.params.id}` })
                .then(res => this.successLoad(res)).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => this.loaded = false);
        },

        async saveClient() {
            if (this.$v.user.$invalid) {
                this.$v.user.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, {
                user: this.user,
                clientDetails: this.clientDetails
            });

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                    this.$v.user.$reset();
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        if (error.response.data.error == 'invalidPhone') {
                            this.$v.user.phoneNumber.$touch();
                            this.user.phoneNumber = null;
                        }

                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        successLoad(res) {
            const { data } = res;

            this.clientDetails = Object.assign({}, {
                id: data.id,
                birthday: data.birthday && data.birthday.format('{yyyy}-{MM}-{dd}')
            });

            this.user = Object.assign({}, Object.select(data.user, ['id', ...Object.keys(this.user)]));

            if (this.user.id && !this.$route.params.id) {
                this.$router.push(`/clients/editor/${this.user.id}`);
            }

            this.$nextTick(() => {
                this.canSaveClient = false;
            });
        }
    }
};
</script>
